/**
 * Betaalmethoden die de kassa per land toont, plus hun logo. Gedeeld met de
 * winkelwagen en de footer, zodat die nooit een methode belooft die de kassa
 * niet biedt.
 *
 * PayPal: de standaard betaalmethode-configuratie in Stripe heeft PayPal aan
 * (gecontroleerd 14 okt 2026) en Stripe Checkout biedt het aan bij
 * EUR-betalingen. De sessie zet geen payment_method_types, dus Stripe kiest
 * zelf per klant; deze lijst is alleen de belofte op onze eigen pagina's.
 */
const defaultPaymentsByLocale: Record<string, string[]> = {
  nl: ['iDEAL', 'Bancontact', 'PayPal', 'Klarna', 'Visa', 'Mastercard', 'Apple Pay', 'Google Pay'],
  de: ['PayPal', 'Klarna', 'SEPA', 'Visa', 'Mastercard', 'Apple Pay', 'Google Pay'],
  fr: ['Cartes Bancaires', 'PayPal', 'Klarna', 'Bancontact', 'Visa', 'Mastercard', 'Apple Pay', 'Google Pay'],
  en: ['Visa', 'Mastercard', 'PayPal', 'Apple Pay', 'Google Pay'],
};

export function getPaymentMethodsForCheckout(countryCode: string, locale: string): string[] {
  const code = String(countryCode || '').toUpperCase();
  if (code === 'BE') {
    return ['Bancontact', 'iDEAL', 'PayPal', 'Klarna', 'Visa', 'Mastercard', 'Apple Pay', 'Google Pay'];
  }
  if (code === 'DE' || code === 'AT') {
    return ['PayPal', 'Klarna', 'SEPA', 'Visa', 'Mastercard', 'Apple Pay', 'Google Pay', 'iDEAL'];
  }
  if (code === 'FR') {
    return ['Cartes Bancaires', 'PayPal', 'Klarna', 'Visa', 'Mastercard', 'Apple Pay', 'Google Pay'];
  }
  if (code === 'NL') {
    return ['iDEAL', 'Bancontact', 'PayPal', 'Klarna', 'Visa', 'Mastercard', 'Apple Pay', 'Google Pay'];
  }
  if (code === 'PL') {
    return ['Klarna', 'PayPal', 'Visa', 'Mastercard', 'Apple Pay', 'Google Pay', 'SEPA'];
  }
  return defaultPaymentsByLocale[locale] || ['Visa', 'Mastercard', 'PayPal', 'Apple Pay', 'Google Pay', 'Klarna', 'SEPA'];
}

/** Footerlogo's per taal (de footer kent het afleverland nog niet). */
const footerPaymentsByLocale: Record<string, string[]> = {
  nl: defaultPaymentsByLocale.nl,
  de: defaultPaymentsByLocale.de,
  fr: defaultPaymentsByLocale.fr,
};

/**
 * Lijsten die ooit als standaardwaarde in de beheerinstellingen stonden. Staat
 * zo'n lijst nog ongewijzigd opgeslagen, dan heeft niemand hem bewust gekozen
 * en tonen we de taalafhankelijke standaard (met PayPal) in plaats daarvan.
 */
const legacyFooterPaymentLists = new Set([
  'ideal, bancontact, visa, mastercard, apple pay, google pay',
  'ideal, bancontact, klarna, visa, mastercard, apple pay, google pay',
]);

export function resolveFooterPaymentMethods(configured: string[], locale: string): string[] {
  const cleaned = configured.map((item) => item.trim()).filter(Boolean);
  const key = cleaned.join(', ').toLowerCase();
  if (cleaned.length > 0 && !legacyFooterPaymentLists.has(key)) return cleaned;
  return footerPaymentsByLocale[locale] || footerPaymentsByLocale.nl;
}

export function defaultPaymentLogo(name: string) {
  const normalized = name.toLowerCase();
  return (
    normalized.includes('ideal') ? '/logos/payments/ideal.svg' :
    normalized.includes('bancontact') ? '/logos/payments/bancontact.svg' :
    normalized.includes('paypal') ? '/logos/payments/paypal.svg' :
    normalized.includes('visa') ? '/logos/payments/visa.svg' :
    normalized.includes('mastercard') ? '/logos/payments/mastercard.svg' :
    normalized.includes('apple') ? '/logos/payments/apple-pay.svg' :
    normalized.includes('google') ? '/logos/payments/google-pay.svg' :
    normalized.includes('klarna') ? '/logos/payments/klarna.svg' :
    normalized.includes('sepa') ? '/logos/payments/sepa.svg' :
    normalized.includes('cartes') ? '/logos/payments/cartes-bancaires.svg' :
    null
  );
}
